import { motion } from 'framer-motion';
import { Phone, Check } from 'lucide-react';
import { Eyebrow } from './luxe';
import { CLINIC } from '../data/clinic';
import { useLang } from '../i18n';

const ICONS = {
  cards: '/pay/cards.png',
  digital: '/pay/digital.png',
  cash: '/pay/cash.png',
};

const ease = [0.22, 1, 0.36, 1];

// Section 05 — Convenient Payment Options (replaces "Areas We Serve").
const PaymentOptions = () => {
  const { t } = useLang();
  const p = t.payment;

  return (
    <section className="py-24 lg:py-32 bg-[#EAE5DD]" data-testid="payment-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl"
        >
          <div className="flex items-center gap-4 mb-5">
            <span className="font-serif text-2xl text-[#1E4E8C]">04</span>
            <span className="h-px w-16 bg-[#1E4E8C]/40" />
            <Eyebrow color="#17407A">{p.eyebrow}</Eyebrow>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1B1815] leading-[1.08]">
            {p.title1} <span className="italic text-[#1E4E8C]">{p.titleItalic}</span>
          </h2>
          <p className="text-[#8B817A] text-[18px] mt-6 leading-relaxed">{p.intro}</p>
        </motion.div>

        {/* Payment methods with 3D icons — clean, no boxes */}
        <div className="mt-14 lg:mt-20 grid gap-10 sm:gap-8 md:grid-cols-3">
          {p.methods.map((m, i) => (
            <motion.div
              key={m.k}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.1, ease }}
              data-testid={`payment-method-${m.k}`}
              className="group flex flex-col items-center text-center"
            >
              <div className="mb-6 flex h-40 w-40 items-center justify-center">
                <img
                  src={ICONS[m.k]}
                  alt={m.t}
                  loading="lazy"
                  className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.06]"
                  style={{ filter: 'drop-shadow(0 20px 28px rgba(30,45,80,0.28))' }}
                />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl text-[#1B1815]">{m.t}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-[#6E655D]">{m.d}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto mt-10 max-w-3xl text-center text-[15px] italic leading-relaxed text-[#8B817A]"
        >
          {p.processNote}
        </motion.p>

        {/* Insurance panel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease }}
          className="mt-16 lg:mt-20 grid items-center gap-10 rounded-3xl bg-[#1B1815] px-8 py-12 lg:grid-cols-[auto_1fr] lg:px-14 lg:py-14"
          data-testid="payment-insurance-panel"
        >
          <div className="flex justify-center lg:justify-start">
            <img
              src="/pay/shield.png"
              alt="Insurance protection"
              loading="lazy"
              className="h-40 w-40 object-contain lg:h-48 lg:w-48"
              style={{ filter: 'drop-shadow(0 22px 30px rgba(0,0,0,0.45))' }}
            />
          </div>
          <div>
            <span className="text-[13px] tracking-[0.3em] uppercase font-medium text-[#B4882F]">{p.insuranceLabel}</span>
            <ul className="mt-6 grid gap-x-8 gap-y-3 sm:grid-cols-2">
              {p.insurancePlans.map((plan) => (
                <li key={plan} className="flex items-start gap-3 font-serif text-lg text-white sm:text-xl">
                  <Check className="mt-1.5 h-4 w-4 shrink-0 text-[#B4882F]" />
                  {plan}
                </li>
              ))}
            </ul>
            <p className="mt-7 max-w-2xl text-[15px] leading-relaxed text-white/70">{p.insuranceNote}</p>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="mt-12 flex flex-col items-center justify-center gap-5 text-center sm:flex-row sm:gap-8">
          <p className="font-serif text-xl text-[#1B1815] sm:text-2xl">{p.ctaText}</p>
          <motion.a
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            href={CLINIC.phoneHref}
            data-testid="payment-call-btn"
            className="inline-flex items-center gap-2 bg-[#1E4E8C] px-8 py-4 text-[15px] font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#17407A]"
          >
            <Phone className="h-4 w-4" /> {p.ctaBtn}
          </motion.a>
        </div>
      </div>
    </section>
  );
};

export default PaymentOptions;
